/**
 * The list-and-edit pattern every configuration page uses.
 *
 * Each page is a table plus a modal editor, and written out per page that is
 * the same twenty lines of load/save/delete/busy/error state nine times over.
 * The copy that gets a fix is never all of them.
 *
 * What this deliberately does NOT do is optimistic updates. A save here changes
 * what a modality is shown within about two seconds, and a row that appears
 * saved and was rejected is exactly the wrong thing to put in front of somebody
 * configuring a clinical system. Every mutation reloads from the server and the
 * table shows what is actually stored.
 */

import { useCallback, useState } from 'react'
import { api, ApiError } from './api'
import { useFetch, type Fetched } from './hooks'

export interface Resource<T> extends Fetched<T[]> {
  /** True while a create/update/delete is in flight. */
  saving: boolean
  /** The last mutation failure, cleared when the next one starts. */
  saveError: string | null
  clearError: () => void
  create: (body: unknown) => Promise<T | null>
  update: (id: number, body: unknown) => Promise<T | null>
  remove: (id: number) => Promise<boolean>
  /** For endpoints that are not /{id} — reorder, status, cancel. */
  action: <R>(path: string, body?: unknown) => Promise<R | null>
}

export function useResource<T>(path: string, pollMs = 0): Resource<T> {
  const fetched = useFetch<T[]>(path, pollMs)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const run = useCallback(
    async <R,>(work: () => Promise<R>): Promise<R | null> => {
      setSaving(true)
      setSaveError(null)
      try {
        const result = await work()
        fetched.reload()
        return result
      } catch (err) {
        setSaveError(err instanceof ApiError ? err.detail : String(err))
        return null
      } finally {
        setSaving(false)
      }
    },
    [fetched],
  )

  return {
    ...fetched,
    saving,
    saveError,
    clearError: () => setSaveError(null),
    create: (body) => run(() => api.post<T>(path, body)),
    update: (id, body) => run(() => api.patch<T>(`${path}/${id}`, body)),
    remove: async (id) => (await run(() => api.delete<void>(`${path}/${id}`))) !== null,
    action: (actionPath, body) => run(() => api.post(actionPath, body)),
  }
}
